import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { createStyles, Flex, Text, Image, Tooltip, ActionIcon, Group } from "@mantine/core";
import { useElementSize } from "@mantine/hooks";
import { useVirtualizer } from "@tanstack/react-virtual";
import { observer } from "mobx-react";
import React, { useCallback, useEffect, useState } from "react";
import { Icon } from "../../../custom-components/icons";
import { User } from "../../../models/models";
import t from "../../../stores/language";
import { DeleteConfirmModal } from "./delete-user-modal";
import usersImage from "../../../assets/img/users.svg";
import { LockUserConfirmationModal } from "./lock-user-confirmation-modal";

export const useStyles = createStyles((theme) => ({
  card: {
    backgroundImage: "linear-gradient(#fff, #f5f5fa)",
    minWidth: "280px",
    width: "280px",
    maxWidth: "280px",
    height: "166px",
    display: "grid",
    gridTemplateColumns: " 1fr 1fr 1fr 1fr 1fr 1fr 1fr",
    gridTemplateRows: "4fr 2fr 2fr 2fr",
    border: `1px solid ${theme.colors.gray[3]}`,
    margin: theme.other.arc.margins[2],
    boxShadow: theme.shadows.lg,
    "&:hover": {
      boxShadow: theme.shadows.xl,
      transform: "scale(1.05)",
    },
  },
  name: {
    fontSize: "0.8rem",
    gridRow: "2 / 3",
    gridColumn: "1/8",
    padding: "4px",
    lineHeight: "1.0rem",
    overflow: "hidden",
    wordBreak: "break-word",
    justifyContent: "flex-start",
    gap: 5,
    "&>div": {
      lineHeight: "1.1rem",
    },
    ".mantine-Text-root": {
      width: "195px",
    },
    span: {
      width: "75px",
    },
  },
  image: {
    gridRow: "1/2",
    gridColumn: "1/8",
    justifySelf: "center",
    margin: "4px",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&>image": {
      height: "48px",
      width: "48px",
    },
  },
  email: {
    fontSize: "0.8rem",
    margin: "0px 4px",
    gridRow: "3/4",
    gridColumn: "1/8",
    overflow: "hidden",
    justifyContent: "flex-start",
    gap: 5,
    wordBreak: "break-word",
    ".mantine-Text-root": {
      width: "230px",
    },
    span: {
      width: "40px",
    },
  },
  actionsRow: {
    gridRow: "4/5",
    gridColumn: "1/8",
    display: "flex",
    overflow: "hidden",
    padding: " 2px 4px 0px 4px",
    alignItems: "center",
    placeContent: "center",
    gap: theme.spacing.md,
  },
  action: {
    width: "26px",
    height: "26px",
    padding: "2px",
    justifyContent: "center",
    cursor: "pointer",
    borderBottom: " 4px solid transparent",
    display: "flex",
    alignItems: "center",
    transition: "border",
    "&:hover": {
      borderBottom: "4px solid #0091ff ",
    },
  },
}));

export interface UserItems {
  users: UserCardItem[];
}
export interface UserCardItem {
  user: User;
  actions: ItemAction[];
}

type askForConfirmationType = "delete" | "lock";

export interface ItemAction {
  icon: IconProp;
  text: string;
  color: string;
  askForConfirm?: askForConfirmationType;
  action: () => void;
}

const CardView = observer(function CardView(props: UserItems) {
  const { classes } = useStyles();
  const { ref } = useElementSize();
  const [width, setWidth] = useState<number>(window.innerWidth - 30);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [currentAction, setCurrentAction] = useState<ItemAction | null>(null);
  const [deleteModalVisisble, setDeleteModalVisisble] = useState<boolean>(false);
  const [lockUserModalVisible, setLockUserModalVisible] = useState<boolean>(false);

  const itemsPerRow = width === 0 ? 6 : Math.floor(width / 300);

  useEffect(() => {
    const resizeListener = () => {
      setWidth(window.innerWidth - 30);
    };
    window.addEventListener("resize", resizeListener);
    return () => {
      window.removeEventListener("resize", resizeListener);
    };
  }, []);

  const noOfRows = Math.ceil((props.users ?? []).length / itemsPerRow);
  const virtualiser = useVirtualizer({
    count: noOfRows,
    getScrollElement: () => ref.current,
    estimateSize: React.useCallback(() => 166, []),
    overscan: 20,
  });
  const onAction = useCallback((item: User, action: ItemAction) => {
    if (action.askForConfirm === "delete") {
      setDeleteModalVisisble(true);
      setSelectedUser(item);
      setCurrentAction(action);
      return;
    }
    if (action.askForConfirm === "lock") {
      setLockUserModalVisible(true);
      setSelectedUser(item);
      setCurrentAction(action);
      return;
    }
    action.action();
  }, []);
  const deleteCancel = () => {
    setDeleteModalVisisble(false);
    setSelectedUser(null);
    setCurrentAction(null);
  };

  const lockUserCancel = () => {
    setLockUserModalVisible(false);
    setSelectedUser(null);
    setCurrentAction(null);
  };

  const deletelockUserConfirm = () => {
    currentAction?.action();
    setLockUserModalVisible(false);
    setCurrentAction(null);
    setSelectedUser(null);
  };

  const deleteConfirm = () => {
    currentAction?.action();
    setDeleteModalVisisble(false);
    setCurrentAction(null);
    setSelectedUser(null);
  };

  return (
    <div
      ref={ref}
      style={{
        height: `${virtualiser.getTotalSize()}px`,
        width: "100%",
        position: "relative",
      }}
    >
      {virtualiser.getVirtualItems().map((row) => {
        const subItems = (props.users ?? []).slice(row.index * itemsPerRow, row.index * itemsPerRow + itemsPerRow);
        return (
          <Flex key={row.key} direction={"row"}>
            {subItems.map((item, i) => {
              return (
                <div key={"card" + i} className={classes.card}>
                  <div className={classes.image}>
                    <Image src={usersImage} style={{ width: "48px", height: "48px" }} />
                  </div>
                  <Group className={classes.name} noWrap>
                    <span> {t("Name : ")}</span>
                    <Text lineClamp={1}>{item.user.name}</Text>
                  </Group>
                  <Group className={classes.email} noWrap>
                    <span> {t("Email : ")}</span>
                    <Text lineClamp={1}>{item.user.email}</Text>
                  </Group>
                  <div className={classes.actionsRow}>
                    {item.actions.map((i, ix) => (
                      <Tooltip label={i.text} key={item.user.id + "" + ix} withArrow>
                        <ActionIcon
                          color={i.color}
                          className={classes.action}
                          onClick={() => {
                            onAction(item.user, i);
                          }}
                        >
                          <Icon icon={i.icon} size="lg" />
                        </ActionIcon>
                      </Tooltip>
                    ))}
                  </div>
                </div>
              );
            })}
          </Flex>
        );
      })}
      <DeleteConfirmModal item={selectedUser} onOk={deleteConfirm} onCancel={deleteCancel} visible={deleteModalVisisble} />
      <LockUserConfirmationModal item={selectedUser} onOk={deletelockUserConfirm} onCancel={lockUserCancel} visible={lockUserModalVisible} />
    </div>
  );
});
export default CardView;
